import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

// Secure token storage key - using a more obscure name
const TOKEN_KEY = 'asv_crm_session';

// Token expiry check (24 hours)
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Sanitize user input to prevent XSS
const sanitizeInput = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/[<>&"']/g, (char) => {
    const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
    return entities[char];
  });
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const storedToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    // Check if token is expired on load
    if (storedToken && isTokenExpired(storedToken)) {
      sessionStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return storedToken;
  });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const clearAuth = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        // Check token expiry client-side first
        if (isTokenExpired(token)) {
          clearAuth();
          setLoading(false);
          return;
        }
        
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            // Token is invalid
            clearAuth();
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          clearAuth();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token, API_URL, clearAuth]);

  // Periodic token expiry check
  useEffect(() => {
    const interval = setInterval(() => {
      if (token && isTokenExpired(token)) {
        clearAuth();
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [token, clearAuth]);

  const login = async (email, password) => {
    // Sanitize inputs
    const sanitizedEmail = sanitizeInput(email.trim().toLowerCase());
    
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: sanitizedEmail, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    // Store in sessionStorage for current session (more secure)
    // Also store in localStorage for persistence across tabs
    sessionStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return { user: data.user, must_change_password: data.must_change_password };
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user && !isTokenExpired(token)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
